// Copyright (c) 2026 SoundDrift. All rights reserved.

const PING_INTERVAL_MS = 10000;
const HEARTBEAT_TIMEOUT_MS = 30000;
const BASE_RECONNECT_DELAY_MS = 500;
const MAX_RECONNECT_DELAY_MS = 15000;
const CONNECT_TIMEOUT_MS = 12000;
const MAX_OUTBOX = 50;
const MAX_OUTBOX_AGE_MS = 45000;

const NEVER_QUEUED = new Set([
  'ping',
  'join_room',
  'create_room',
  'twitch_check_session',
  'twitch_exchange_code'
]);

export class CloudflareSocket {
  constructor(url, options = {}) {
    this.url = url;
    this.options = options;
    this.listeners = {};
    this.id = 'sd-' + Math.random().toString(36).substring(2, 15);
    this.connected = false;

    this.pingIntervalId = null;
    this.heartbeatTimeoutId = null;

    this.connectionState = 'connecting';
    this._outbox = [];
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._connectTimeoutId = null;
    this._closedByUser = false;

    this.visibilityListener = () => {
      if (document.visibilityState === 'visible') {
        this._checkConnection();
      }
    };

    this.focusListener = () => {
      this._checkConnection();
    };

    this.onlineListener = () => {
      this._reconnectAttempts = 0;
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this._checkConnection();
      } else {
        this._teardownSocket();
        this._openSocket();
      }
    };

    this.offlineListener = () => {
      this._setConnectionState('offline');
      this._stopHeartbeat();
    };

    this._armLifecycleListeners();
    this.connect();
  }

  _armLifecycleListeners() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityListener);
      window.addEventListener('focus', this.focusListener);
      window.addEventListener('online', this.onlineListener);
      window.addEventListener('offline', this.offlineListener);
    }
  }

  _disarmLifecycleListeners() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      window.removeEventListener('focus', this.focusListener);
      window.removeEventListener('online', this.onlineListener);
      window.removeEventListener('offline', this.offlineListener);
    }
  }

  connect() {
    if (this._closedByUser) {
      this._closedByUser = false;
      this._reconnectAttempts = 0;
      this._armLifecycleListeners();
    }
    this._openSocket();
  }

  _openSocket() {
    if (this._closedByUser) return;

    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    let wsUrl = this.url || '';
    if (wsUrl.startsWith('http://')) {
      wsUrl = wsUrl.replace('http://', 'ws://');
    } else if (wsUrl.startsWith('https://')) {
      wsUrl = wsUrl.replace('https://', 'wss://');
    } else if (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://')) {
      const proto = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = typeof window !== 'undefined' ? window.location.host : 'localhost:3000';
      wsUrl = `${proto}//${host}`;
    }

    const urlObj = new URL(wsUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');

    const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const optionsRoomCode = this.options?.query?.roomCode || this.options?.roomCode || '';
    const roomCode = optionsRoomCode ||
                     urlObj.searchParams.get('roomCode') ||
                     urlParams.get('roomCode') ||
                     urlParams.get('debug_room') ||
                     urlParams.get('room') ||
                     (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('sounddrift_roomCode') : '') ||
                     '';

    if (roomCode) {
      urlObj.searchParams.set('roomCode', roomCode.toUpperCase());
    }

    if (urlObj.pathname === '/' || urlObj.pathname === '') {
      urlObj.pathname = '/ws';
    }

    if (this.connectionState !== 'connected') {
      this._setConnectionState(this._reconnectAttempts > 0 ? 'reconnecting' : 'connecting');
    }

    let ws;
    try {
      ws = new WebSocket(urlObj.toString());
    } catch (err) {
      console.error('[CloudflareSocket] WebSocket constructor error:', err);
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;

    this._connectTimeoutId = setTimeout(() => {
      this._connectTimeoutId = null;
      if (this.ws === ws && ws.readyState === WebSocket.CONNECTING) {
        this._teardownSocket();
        this._scheduleReconnect();
      }
    }, CONNECT_TIMEOUT_MS);

    ws.onopen = () => {
      if (this.ws !== ws) return;
      this._clearConnectTimeout();
      this.connected = true;
      this._reconnectAttempts = 0;
      this._setConnectionState('connected');
      this._trigger('connect');
      this._startHeartbeat();
      this._flushOutbox();
    };

    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      this._resetHeartbeatTimeout();
      try {
        const payload = JSON.parse(event.data);
        if (payload && payload.event) {
          if (payload.event === 'pong') return;
          this._trigger(payload.event, payload.data);
        }
      } catch (err) {
        console.error('[CloudflareSocket] Error parsing WS message:', err);
      }
    };

    ws.onclose = (event) => {
      if (this.ws !== ws) return;
      this._clearConnectTimeout();
      const wasConnected = this.connected;
      this.connected = false;
      this._stopHeartbeat();
      if (wasConnected) this._trigger('disconnect');
      this._scheduleReconnect();
    };

    ws.onerror = (err) => {
      if (this.ws !== ws) return;
      this._trigger('connect_error', err);
    };
  }

  on(event, fn) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(fn);
    return this;
  }

  off(event, fn) {
    if (!this.listeners[event]) return this;
    if (!fn) {
      delete this.listeners[event];
    } else {
      this.listeners[event] = this.listeners[event].filter(l => l !== fn);
    }
    return this;
  }

  emit(event, data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ event, data }));
        return this;
      } catch (err) {
        this._handleConnectionLoss();
      }
    }
    this._queue(event, data);
    return this;
  }

  _queue(event, data) {
    if (NEVER_QUEUED.has(event)) return;
    if (this._outbox.length >= MAX_OUTBOX) {
      this._outbox.shift();
    }
    this._outbox.push({ event, data, queuedAt: Date.now() });
    this._emitConnectionState();
  }

  _flushOutbox() {
    if (!this._outbox.length) return;
    const now = Date.now();
    const pending = this._outbox;
    this._outbox = [];
    for (const item of pending) {
      if (now - item.queuedAt > MAX_OUTBOX_AGE_MS) continue;
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ event: item.event, data: item.data }));
        } catch {
          this._outbox.push(item);
        }
      } else {
        this._outbox.push(item);
      }
    }
  }

  disconnect() {
    this._closedByUser = true;
    this._disarmLifecycleListeners();
    this._outbox = [];
    this._teardownSocket();
    this._setConnectionState('offline');
    return this;
  }

  _clearConnectTimeout() {
    if (this._connectTimeoutId) {
      clearTimeout(this._connectTimeoutId);
      this._connectTimeoutId = null;
    }
  }

  _teardownSocket() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._clearConnectTimeout();
    this._stopHeartbeat();

    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try {
        ws.close();
      } catch (e) {}
    }
    this.connected = false;
  }

  _scheduleReconnect() {
    if (this._closedByUser || this._reconnectTimer) return;

    this._setConnectionState(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'reconnecting');

    const base = BASE_RECONNECT_DELAY_MS;
    const capped = Math.min(MAX_RECONNECT_DELAY_MS, base * Math.pow(2, this._reconnectAttempts));
    const delay = Math.round(capped * (0.7 + Math.random() * 0.6));
    this._reconnectAttempts++;

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._openSocket();
    }, delay);
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this.pingIntervalId = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ event: 'ping', data: {} }));
        } catch {
          this._handleConnectionLoss();
        }
      }
    }, PING_INTERVAL_MS);

    this._resetHeartbeatTimeout();
  }

  _resetHeartbeatTimeout() {
    if (this.heartbeatTimeoutId) clearTimeout(this.heartbeatTimeoutId);
    this.heartbeatTimeoutId = setTimeout(() => {
      this._handleConnectionLoss();
    }, HEARTBEAT_TIMEOUT_MS);
  }

  _stopHeartbeat() {
    if (this.pingIntervalId) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
    if (this.heartbeatTimeoutId) {
      clearTimeout(this.heartbeatTimeoutId);
      this.heartbeatTimeoutId = null;
    }
  }

  _handleConnectionLoss() {
    const wasConnected = this.connected;
    this._teardownSocket();
    if (wasConnected) this._trigger('disconnect');
    this._scheduleReconnect();
  }

  _checkConnection() {
    if (this._closedByUser) return;
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
      if (this._reconnectTimer) {
        clearTimeout(this._reconnectTimer);
        this._reconnectTimer = null;
      }
      this._openSocket();
    } else if (this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ event: 'ping', data: {} }));
      } catch (e) {
        this._handleConnectionLoss();
      }
    }
  }

  _setConnectionState(state) {
    if (this.connectionState === state) return;
    this.connectionState = state;
    this._emitConnectionState();
  }

  _emitConnectionState() {
    this._trigger('connection_state', {
      state: this.connectionState,
      attempts: this._reconnectAttempts,
      pending: this._outbox.length
    });
  }

  _trigger(event, data) {
    const handlers = this.listeners[event];
    if (handlers) {
      for (const h of [...handlers]) {
        try {
          h(data);
        } catch (err) {
          console.error(`[CloudflareSocket] Listener for "${event}" error:`, err);
        }
      }
    }
  }
}
